import React, { useEffect, useRef } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import loadable from '@loadable/component';

import { sendVerificationEmail, hasCurrentUserErrors } from '../../ducks/user.duck';
import { logout, authenticationInProgress } from '../../ducks/auth.duck';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import { fetchStripeAccount } from '../../ducks/stripeConnectAccount.duck';

const Topbar = loadable(() => import(/* webpackChunkName: "Topbar" */ './Topbar/Topbar'));

/**
 * Topbar container component, which is connected to Redux Store.
 * @component
 * @param {Object} props
 * @param {number} props.notificationCount number of notifications
 * @param {Function} props.onLogout logout function
 * @param {Function} props.onManageDisableScrolling manage disable scrolling function
 * @param {Function} props.onResendVerificationEmail resend verification email function
 * @param {Object} props.sendVerificationEmailInProgress send verification email in progress
 * @param {Object} props.sendVerificationEmailError send verification email error
 * @param {boolean} props.hasGenericError has generic error
 * @returns {JSX.Element}
 */
export const TopbarContainerComponent = props => {
  const {
    notificationCount = 0,
    hasGenericError,
    needsStripeAccountFetch,
    onFetchStripeAccount,
    ...rest
  } = props;

  // The verification banner needs Stripe's `requirements`, which only
  // `sdk.stripeAccount.fetch()` returns — the account included with
  // currentUser carries just the id. Nothing fetched it outside the payout and
  // edit-listing pages, so the banner showed only if you had just been there
  // and vanished again on the next page. Fetch it once per session, and only
  // for a seller who actually has a connected account.
  const hasFetchedRef = useRef(false);
  useEffect(() => {
    if (needsStripeAccountFetch && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      onFetchStripeAccount();
    }
  }, [needsStripeAccountFetch, onFetchStripeAccount]);

  return (
    <Topbar notificationCount={notificationCount} showGenericError={hasGenericError} {...rest} />
  );
};

const mapStateToProps = state => {
  // Topbar needs isAuthenticated and isLoggedInAs
  const { isAuthenticated, isLoggedInAs, logoutError, authScopes } = state.auth;
  // Topbar needs user info.
  const {
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    currentUserSaleNotificationCount = 0,
    currentUserOrderNotificationCount = 0,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
  } = state.user;
  const hasGenericError = !!(logoutError || hasCurrentUserErrors(state));

  // Surface a global banner when the seller's Stripe account has
  // outstanding verification requirements *after* it was set up (i.e. a
  // bank account is on file but Stripe now needs more — e.g. extra KYC,
  // an expiring document). The connected account is saved to the store
  // app-wide when currentUser loads, so no extra fetch is needed. We gate
  // on an attached external account so this never fires during the normal
  // initial onboarding, only when genuine further action is required.
  const { stripeAccount } = state.stripeConnectAccount || {};
  const stripeAccountData = stripeAccount?.attributes?.stripeAccountData || null;
  const requirements = stripeAccountData?.requirements;
  const hasDue = arr => Array.isArray(arr) && arr.length > 0;
  const externalAccountAttached = (stripeAccountData?.external_accounts?.data?.length || 0) > 0;
  const showStripeVerificationNotice =
    !!stripeAccount?.id &&
    externalAccountAttached &&
    (hasDue(requirements?.past_due) || hasDue(requirements?.currently_due));

  // Only worth a call when the user has an account whose data we lack, and
  // never while one is already in flight.
  const {
    fetchStripeAccountInProgress,
    fetchStripeAccountError,
  } = state.stripeConnectAccount || {};
  const needsStripeAccountFetch =
    !!currentUser?.stripeAccount?.id &&
    !stripeAccountData &&
    !fetchStripeAccountInProgress &&
    !fetchStripeAccountError;

  return {
    authInProgress: authenticationInProgress(state),
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    notificationCount: currentUserSaleNotificationCount + currentUserOrderNotificationCount,
    isAuthenticated,
    isLoggedInAs,
    authScopes,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    hasGenericError,
    showStripeVerificationNotice,
    needsStripeAccountFetch,
  };
};

const mapDispatchToProps = dispatch => ({
  onLogout: historyPush => dispatch(logout(historyPush)),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onResendVerificationEmail: () => dispatch(sendVerificationEmail()),
  // Swallow the rejection: a failed lookup must never surface as an unhandled
  // error in the topbar. The error state in the store stops it retrying.
  onFetchStripeAccount: () => dispatch(fetchStripeAccount()).catch(() => {}),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const TopbarContainer = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(TopbarContainerComponent);

export default TopbarContainer;
